/* src/contexts/AuthContext.tsx - CONTEXTO DE AUTENTICACIÓN */
/* ========================================= */

import React, { createContext, useContext, useState, ReactNode } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  orders: Order[];
  enrolledCourses: string[];
}

interface Order {
  id: string;
  date: string;
  items: any[];
  total: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: Partial<User>) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  const login = async (email: string, password: string): Promise<boolean> => {
    // Simulación de login - En producción conectar con API
    try {
      // Simulamos un delay de API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Usuario mock para demo
      const mockUser: User = {
        id: '1',
        name: 'Usuario Demo',
        email: email,
        orders: [],
        enrolledCourses: []
      };
      
      setUser(mockUser);
      return true;
    } catch (error) {
      return false;
    }
  };

  const register = async (userData: Partial<User>): Promise<boolean> => {
    // Simulación de registro - En producción conectar con API
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newUser: User = {
        id: Date.now().toString(),
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone,
        company: userData.company,
        orders: [],
        enrolledCourses: []
      };
      
      setUser(newUser);
      return true;
    } catch (error) {
      return false;
    }
  };

  const logout = () => {
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};